import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, IconButton, Stack, Typography, useTheme } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form';
import { IVehicleStatusPhotosForm } from '../../models/models';
import photosFormConfig from './photos-form-config';
import PhotoFileField from './PhotoFileField';
import { useTranslation } from 'react-i18next';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { GridCloseIcon } from '@mui/x-data-grid';
import { WarningAmberRounded } from '@mui/icons-material';

const toBase64 = (file: Blob): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
});

export type TFileRefs = {
    [k in keyof IVehicleStatusPhotosForm]: MutableRefObject<HTMLInputElement | null>
}

export type TFilePayload = {
    content: string,
    type: string,
    contenttype: string,
    size: number,
}

const IMAGE_TYPE_MAPPING: { [k in keyof IVehicleStatusPhotosForm]: string } = {
    front: "front",
    right: "right side",
    rear: "rear",
    left: "left side",
    frontSeats: "inside (front seats)",
    rearSeats: "inside (rear seats)",
    trunk: "trunk (recharging cable and security kit)",
    frWheel: "front right wheel",
    flWheel: "front left wheel",
    rrWheel: "rear right wheel",
    rlWheel: "rear left wheel",
    additionalPhoto1: "additional image 1",
    additionalPhoto2: "additional image 2",
    additionalPhoto3: "additional image 3",
}

// export type TFilePayload = {
//     rentalAgreementId: string,
//     images: {
//         content: string,
//         type: string,
//         contenttype: string,
//         size: number,
//     }[],
//     vehicleId: string,
//     tenant: string,
//     dealer: string,
//     scenario: string,
// }

type AddPhotosProps = {
    open: boolean,
    onClose: () => void,
    onSubmit: (files: TFilePayload[]) => void,
}

function AddPhotos(props: AddPhotosProps) {
    const {t} = useTranslation(['network', 'translation']);
    const theme = useTheme();
    const [confirmClose, setConfirmClose] = useState(false);

    const fileRefs: TFileRefs = {
        front: useRef<HTMLInputElement>(null),
        right: useRef<HTMLInputElement>(null),
        rear: useRef<HTMLInputElement>(null),
        left: useRef<HTMLInputElement>(null),
        frontSeats: useRef<HTMLInputElement>(null),
        rearSeats: useRef<HTMLInputElement>(null),
        trunk: useRef<HTMLInputElement>(null),
        frWheel: useRef<HTMLInputElement>(null),
        flWheel: useRef<HTMLInputElement>(null),
        rrWheel: useRef<HTMLInputElement>(null),
        rlWheel: useRef<HTMLInputElement>(null),
        additionalPhoto1: useRef<HTMLInputElement>(null),
        additionalPhoto2: useRef<HTMLInputElement>(null),
        additionalPhoto3: useRef<HTMLInputElement>(null),
    }

    const photosForm = useForm<IVehicleStatusPhotosForm>(photosFormConfig(t));

    async function handleSubmit(files: IVehicleStatusPhotosForm) {
        const imagePromises = (Object.keys(fileRefs) as Array<keyof TFileRefs>)
            .map(async (imageName): Promise<TFilePayload | null> => {
                const file = fileRefs[imageName].current!.files![0];
                return !!file
                    ? {
                        content: await toBase64(file),
                        type: IMAGE_TYPE_MAPPING[imageName],
                        contenttype: file.type,
                        size: file.size,
                    }
                    : Promise.resolve(null)
            });

        const images = await Promise.all(imagePromises);
        props.onSubmit(images.filter(img => img !== null) as TFilePayload[]);
    }

    useEffect(() => {
        if (props.open === false) {
            photosForm.reset();
        }
    }, [props.open])

    return (
        <FormProvider {...photosForm}>
            <Dialog open={props.open} fullWidth={true} maxWidth={'sm'}>
                <DialogTitle>{t('raCheckOutIn.checkDamage.button.addStatusPictures')}</DialogTitle>
                <IconButton
                    onClick={() => setConfirmClose(true)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme.palette.grey[500],
                    }}
                >
                    <GridCloseIcon />
                </IconButton>
                <DialogContent>
                    <PhotoFileField name='front' fileRefs={fileRefs} required={true} />
                    <PhotoFileField name='frWheel' fileRefs={fileRefs} required={true} />
                    <PhotoFileField name='right' fileRefs={fileRefs} required={true} />
                    <PhotoFileField name='rrWheel' fileRefs={fileRefs} required={true} />
                    <PhotoFileField name='rear' fileRefs={fileRefs} required={true} />
                    <PhotoFileField name='trunk' fileRefs={fileRefs} required={true} />
                    <PhotoFileField name='rlWheel' fileRefs={fileRefs} required={true} />
                    <PhotoFileField name='left' fileRefs={fileRefs} required={true} />
                    <PhotoFileField name='flWheel' fileRefs={fileRefs} required={true} />
                    <PhotoFileField name='frontSeats' fileRefs={fileRefs} required={true} />
                    <PhotoFileField name='rearSeats' fileRefs={fileRefs} required={true} />
                    <PhotoFileField name='additionalPhoto1' fileRefs={fileRefs} required={false} />
                    <PhotoFileField name='additionalPhoto2' fileRefs={fileRefs} required={false} />
                    <PhotoFileField name='additionalPhoto3' fileRefs={fileRefs} required={false} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={photosForm.handleSubmit(handleSubmit)}>{t('raCheckOutIn.checkDamage.button.continue')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmClose}
                onClose={() => setConfirmClose(false)}
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" justifyContent="center">
                        <Typography variant="h2" fontWeight="bold">{t("raCheckOutIn.checkDamage.addStatusPictures.closeConfirm.title")}</Typography>
                        <div style={{ margin: theme.spacing(0.25) }}></div>
                        <WarningAmberRounded color="warning" style={{ fontSize: 48 }} />
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t("raCheckOutIn.checkDamage.addStatusPictures.closeConfirm.body")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setConfirmClose(false); props.onClose(); }} autoFocus>{t("raCheckOutIn.checkDamage.addStatusPictures.closeConfirm.confirm")}</Button>
                    <Button onClick={() => setConfirmClose(false)} autoFocus>{t("raCheckOutIn.checkDamage.addStatusPictures.closeConfirm.cancel")}</Button>
                </DialogActions>
            </Dialog>
        </FormProvider>
    )
}

export default AddPhotos;
